import baseApi from 'shared/api/baseApi';

import type { MediaCollection } from '../lib/mediaEntries';
import type { Project, ProjectUploadResponse } from '../model/project';
import type { ProjectDetail } from '../ui/project-detail.types';

type ProjectFilesMutationArgs = { projectId: number; files: File[] };
type ProjectMultimediaItem = { collection: MediaCollection; fileName: string };
type ProjectDetailResponse = Project & ProjectDetail;

const createProjectFilesFormData = (files: File[]) => {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append('file', file);
  });
  return formData;
};

const encodeFileName = (fileName: string) => encodeURIComponent(fileName);

const projectsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getProjects: build.query<Project[], void>({
      query: () => ({ url: '/admin/projects', method: 'GET' }),
      providesTags: ['MasterProject'],
    }),
    getProject: build.query<ProjectDetailResponse, number>({
      query: (projectId) => ({ url: `/admin/projects/${projectId}`, method: 'GET' }),
      providesTags: ['MasterProject'],
    }),
    uploadProjectImages: build.mutation<ProjectUploadResponse, ProjectFilesMutationArgs>({
      query: ({ projectId, files }) => ({
        url: `/admin/projects/${projectId}/images`,
        method: 'POST',
        body: createProjectFilesFormData(files),
      }),
      invalidatesTags: ['MasterProject'],
    }),
    uploadProjectMultimedia: build.mutation<ProjectUploadResponse, ProjectFilesMutationArgs>({
      query: ({ projectId, files }) => ({
        url: `/backoffice/projects/${projectId}/multimedia/upload`,
        method: 'POST',
        body: createProjectFilesFormData(files),
      }),
      invalidatesTags: ['MasterProject'],
    }),
    uploadProjectQuotes: build.mutation<ProjectUploadResponse, ProjectFilesMutationArgs>({
      query: ({ projectId, files }) => ({
        url: `/backoffice/projects/${projectId}/quotes`,
        method: 'POST',
        body: createProjectFilesFormData(files),
      }),
      invalidatesTags: ['MasterProject'],
    }),
    uploadProjectFiles: build.mutation<ProjectUploadResponse, ProjectFilesMutationArgs>({
      query: ({ projectId, files }) => ({
        url: `/backoffice/projects/${projectId}/files`,
        method: 'POST',
        body: createProjectFilesFormData(files),
      }),
      invalidatesTags: ['MasterProject'],
    }),
    deleteProjectImage: build.mutation<unknown, { projectId: number; fileName: string }>({
      query: ({ projectId, fileName }) => ({
        url: `/backoffice/projects/${projectId}/images/${encodeFileName(fileName)}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['MasterProject'],
    }),
    deleteProjectQuote: build.mutation<unknown, { projectId: number; fileName: string }>({
      query: ({ projectId, fileName }) => ({
        url: `/backoffice/projects/${projectId}/quotes/${encodeFileName(fileName)}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['MasterProject'],
    }),
    deleteProjectFile: build.mutation<unknown, { projectId: number; fileName: string }>({
      query: ({ projectId, fileName }) => ({
        url: `/backoffice/projects/${projectId}/files/${encodeFileName(fileName)}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['MasterProject'],
    }),
    duplicateProjectMultimedia: build.mutation<unknown, { projectId: number; items: ProjectMultimediaItem[] }>({
      query: ({ projectId, items }) => ({
        url: `/backoffice/projects/${projectId}/multimedia/duplicate`,
        method: 'POST',
        body: { items },
      }),
      invalidatesTags: ['MasterProject'],
    }),
    downloadProjectMultimediaFile: build.mutation<Blob, { projectId: number; path: string }>({
      query: ({ projectId, path }) => ({
        url: `/backoffice/projects/${projectId}/multimedia/download?path=${encodeURIComponent(path)}`,
        method: 'GET',
        responseHandler: (response) => response.blob(),
        cache: 'no-cache',
      }),
    }),
    downloadProjectMultimediaZip: build.mutation<Blob, { projectId: number; paths: string[] }>({
      query: ({ projectId, paths }) => ({
        url: `/backoffice/projects/${projectId}/multimedia/download-zip`,
        method: 'POST',
        body: { paths },
        responseHandler: (response) => response.blob(),
        cache: 'no-cache',
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useDeleteProjectFileMutation,
  useDeleteProjectImageMutation,
  useDeleteProjectQuoteMutation,
  useDownloadProjectMultimediaFileMutation,
  useDownloadProjectMultimediaZipMutation,
  useDuplicateProjectMultimediaMutation,
  useGetProjectQuery,
  useGetProjectsQuery,
  useUploadProjectFilesMutation,
  useUploadProjectImagesMutation,
  useUploadProjectMultimediaMutation,
  useUploadProjectQuotesMutation,
} = projectsApi;
